// src/dash/page.tsx
import { useEffect, useState } from "react";
import { Box, Card, Container, Flex, Grid, Heading, Link, Skeleton, Text, Tooltip } from "@radix-ui/themes";

import ProfileDetails from "../components/ProfileDetails";
import { getScenarios, warmupFeedbackAgent } from "../helper/api";
import type { Scenario } from "../helper/api";

export default function Dash() {
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setError(null);

        const data = await getScenarios();
        if (!cancelled) setScenarios(data);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || "Failed to load scenarios");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    try {
      warmupFeedbackAgent();
    } catch (error) {
      console.error('Feedback warmup error: \n', error)
    }

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Container p="4">
      <Grid columns={{ sm: "2", initial: "1" }} gap="6">
        <Box>
          <ProfileDetails name="Dr. Person" email="person@mayoclinic.org" image="/avatars/coach" />
        </Box>

        <Box>
          <Card size="2" variant="classic">
            <Box>
              <Heading as="h1" size="6">
                Coaching Skills
              </Heading>

              <Flex gap="3" direction="column" className="scenario-links">
                <Box className="mt-1 mb-4">
                  <Text as="p">Select a skill to practice</Text>
                </Box>

                {/* Loading skeletons */}
                {loading && (
                  <>
                    <Card size="2" variant="classic">
                      <Skeleton my="2" className="w-md" />
                      <Skeleton my="2" className="w-xs" />
                    </Card>
                    <Card size="2" variant="classic">
                      <Skeleton my="2" className="w-md" />
                      <Skeleton my="2" className="w-xs" />
                    </Card>
                    <Card size="2" variant="classic">
                      <Skeleton my="2" className="w-md" />
                      <Skeleton my="2" className="w-xs" />
                    </Card>
                  </>
                )}

                {/* Error state */}
                {!loading && error && (
                  <Card size="2" variant="classic">
                    <Text as="p">Error loading scenarios: {error}</Text>
                  </Card>
                )}

                {/* Success state */}
                {!loading &&
                  !error &&
                  scenarios.map((s) => (
                    <Link key={s.scenario_id} href={`/scenario?id=${s.scenario_id}`}>
                      <Card size="2" variant="classic">
                        <Heading as="h3" size="4">
                          {s.scenario_name} &raquo;
                        </Heading>
                        <Tooltip content={s.description}>
                          <Text
                            as="p"
                            style={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              display: 'block'
                            }}
                          >
                            {s.description}
                          </Text>
                        </Tooltip>
                      </Card>
                    </Link>
                  ))}
              </Flex>
            </Box>
          </Card>
        </Box>
      </Grid>
    </Container>
  );
}
